<template>
  <VModal :value="modelValue" @click:outside="close">
    <template #headerAction>
      <VBtn icon="close" small @click="close" />
    </template>

    <template v-if="mode === ModalMode.Broadcast" #headerTitle>
      {{ time }}
    </template>

    <template #default>
      <div v-if="item">
        <div class="program">
          <div class="program__thumb" :class="item.channel">
            <VImg :src="getProgramThumbnail(item.id, futurePrograms, thumbnailUrl)" :aspect-ratio="16 / 9" contain />
          </div>

          <div class="program__top">
            <div class="program__title">
              {{ formatTitle(item.title) }}
              <template v-for="code in getScheduleTypeCodes(item.title)" :key="code.id">
                <VSquareSymbol class="program__type-code">{{ getScheduleTypeCharacter(code) }}</VSquareSymbol>
              </template>
            </div>
            <div v-if="mode === ModalMode.Broadcast && isEptitle(item.eptitle)" class="program__eptitle">
              {{ item.eptitle }}
            </div>
            <div class="program__description">
              <template v-if="mode === ModalMode.Broadcast">
                <VTextAccordion :lines="4" :text="item.epdescription || item.description || ''" />
              </template>
              <template v-else>
                <VTextAccordion :lines="4" :text="item.description || ''" />
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footerAction>
      <div v-if="item?.id" class="action-footer">
        <VBtn outlined rounded @click="openDetail(item)">詳細情報</VBtn>

        <div v-if="mode !== ModalMode.Mylist" class="action-footer__mylist-button">
          <BaseIcon size="24" @click.stop="toggleMyList()"><component :is="iconMyList"></component></BaseIcon>
        </div>
      </div>

      <div v-if="mode !== ModalMode.Broadcast && futurePrograms?.length" class="future-program">
        <div class="future-program__title">放送時間</div>
        <div v-for="(futureProgram, index) in futurePrograms" :key="index" class="future-program__item">
          <div class="future-program__item-detail">
            <template v-if="futureProgram.start && futureProgram.end">
              <p class="future-program__time">{{ datetimeFormat(futureProgram.start).split(')')[0] }})</p>
              <p class="future-program__time">
                {{ tbsDatetimeStartEndFormat(futureProgram.start, futureProgram.end) }}
              </p>

              <div class="future-program__program-title">
                <p>
                  <span v-if="futureProgram.eptitle?.trim().length">
                    {{ `${formatTitle(futureProgram.eptitle)} #${Number(futureProgram.episode)}` }}
                  </span>
                  <span v-else>
                    {{ `${formatTitle(futureProgram.title)}` }}
                  </span>
                </p>
              </div>
            </template>

            <template v-else>
              <p class="future-program__time">{{ futureProgram.onairPlan }}</p>
            </template>
          </div>

          <div v-if="futureProgram.channel" class="future-program__channel">
            <VChannelLabel :ch="futureProgram.channel === 'tch1' ? 1 : 2" />
          </div>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script setup lang="ts">
import { GtmSupport, useGtm } from '@gtm-support/vue-gtm';
import dayjs from 'dayjs';
import { computed, inject } from 'vue';
import { ToggleMylistReq } from '@/@types/MyList';
import VBtn from '@/components/atoms/VBtn/VBtn.vue';
import VImg from '@/components/atoms/VImg/VImg.vue';
import VSquareSymbol from '@/components/atoms/VSquareSymbol/VSquareSymbol.vue';
import VTextAccordion from '@/components/atoms/VTextAccordion/VTextAccordion.vue';
import IconMyListOff from '@/components/atoms/icons/IconMyListOff.vue';
import IconMyListOn from '@/components/atoms/icons/IconMyListOn.vue';
import VModal from '@/components/molecules/VModal/VModal.vue';
import { BlackListKey } from '@/composables/useBlacklist';
import { useFlutterConnection } from '@/composables/useFlutterConnection';
import { GlobalStoreKey } from '@/composables/useGlobalStore';
import { MyListKey } from '@/composables/useMyList';
import { ProgramModalKey } from '@/composables/useProgramModal';
import { ScheduleKey } from '@/composables/useSchedule';
import { useToast } from '@/composables/useToast';
import { COMMON_IMG_URL } from '@/constants/BlackList';
import { ModalProgram } from '@/modules/ModalProgram';
import { Program, _Program } from '@/modules/Program';
import { datetimeFormat, tbsDatetimeStartEndFormat } from '@/utils/index';

const scheduleInjection = inject(ScheduleKey);
if (!scheduleInjection) throw new Error(`${ScheduleKey} NOT PROVIDE`);
const { formatTitle, isEptitle, getScheduleTypeCodes, getScheduleTypeCharacter } = scheduleInjection;

const globalStoreInjection = inject(GlobalStoreKey);
if (!globalStoreInjection) throw new Error(`${GlobalStoreKey} NOT PROVIDE`);
const { userId, startLoading, stopLoading, loading } = globalStoreInjection;

const myListInjection = inject(MyListKey);
if (!myListInjection) throw new Error(`${MyListKey} NOT PROVIDE`);
const { toggleMyListState, getMyListData } = myListInjection;

const programModalInjection = inject(ProgramModalKey);
if (!programModalInjection) throw new Error(`${ProgramModalKey} NOT PROVIDE`);
const { close } = programModalInjection;

const blackListInjection = inject(BlackListKey);
if (!blackListInjection) throw new Error(`${BlackListKey} NOT PROVIDE`);
const { getProgramThumbnail } = blackListInjection;

const ModalMode = {
  /**
   * - 番組単位のモーダル（マイリストボタン付）
   */
  Program: 'modeProgram',
  /**
   * - 放送単位のモーダル（番組表モーダル）
   */
  Broadcast: 'modeBroadcast',
  /**
   * - 番組単位のモーダル（マイリストボタンなし）
   */
  Mylist: 'modeMylist',
} as const;

interface Props {
  modelValue: boolean;
  item?: ModalProgram;
  mode: typeof ModalMode[keyof typeof ModalMode];
  futurePrograms: (Program | _Program)[];
}

const props = defineProps<Props>();

/**
 * TBS時間に変換した時間を返す
 */
const time = computed(() => {
  if (!props.item?.start) return;
  const d = dayjs(props.item?.start);
  const hour = d.hour() % 12;
  return d.hour() < 4 ? d.format(`深夜${hour}:mm`) : d.format(`A${hour}:mm`);
});

const thumbnailUrl = computed(() => {
  if (!props.item) throw new Error('指定プログラムがありません');
  return props.item.epthumbnailUrl || props.item.thumbnailUrl || COMMON_IMG_URL;
});

const GTM: GtmSupport | undefined = useGtm();
const { openNewWebView } = useFlutterConnection();
const openDetail = (program?: ModalProgram) => {
  if (!program?.page) return;

  // Track 詳細情報 button click event
  GTM?.trackEvent({
    event: '詳細情報 button',
    action: 'click',
    program_title: program?.title,
    next_page: program?.page,
    use_uid: userId?.value,
    value: 5000,
    noninteraction: false,
  });
  openNewWebView(program?.page);
};

const iconMyList = computed(() => {
  if (props.item?.id) {
    return getMyListData(props.item?.id) ? IconMyListOn : IconMyListOff;
  } else {
    return IconMyListOff;
  }
});

const { activateToast } = useToast();

const toggleMyList = async () => {
  if (!props.item || loading.value) return;

  const requestData: ToggleMylistReq = {
    id: props.item.id,
    title: props.item.title,
    episode: props.item.episode,
    start: props.item.start,
    page: props.item.page,
  };

  startLoading();
  await toggleMyListState(userId.value as number, requestData);
  activateToast('myList', getMyListData(props.item?.id) ? 'add' : 'delete');
  stopLoading();
};
</script>

<style lang="scss" scoped>
.listed {
  background-color: $color-accent2;
}

.action-header {
  text-align: right;
  margin-right: -5px;
}

.action-footer {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;

  &__mylist-button {
    position: absolute;
    right: 0;
    bottom: 0;
  }
}

.program {
  &__time {
    font-size: 16px;
    color: $color-grey-light2;
    margin-bottom: 10px;
  }

  &__thumb {
    position: relative;
    margin-bottom: 20px;

    &.tch1::before {
      content: '';
      position: absolute;
      bottom: -7px;
      display: block;
      width: 100%;
      height: 4px;
      background: #0050ff;
    }

    &.tch2::before {
      content: '';
      position: absolute;
      bottom: -7px;
      display: block;
      width: 100%;
      height: 4px;
      background: $color-secondary;
    }
  }

  &__title {
    font-size: 16px;
    margin-bottom: 15px;
    line-height: 1.5;
  }

  &__eptitle {
    font-size: 16px;
    margin-bottom: 15px;
    line-height: 1.5;
  }

  &__description {
    color: $color-grey-light2;
    font-size: 14px;
    margin-bottom: 15px;
    line-height: 1.5;
  }
}

.future-program {
  margin-top: 24px;

  &__title {
    text-align: left;
    font-size: 22px;
    font-weight: 300;
    line-height: 22px;
    letter-spacing: 0;
    color: $color-base;
  }

  &__item {
    border-top: 1px $bg-dark solid;
    width: 100%;
    padding: 16px 0;
    display: flex;
    justify-content: space-between;
    text-align: left;

    &:nth-of-type(2) {
      border: none;
    }
  }

  &__time {
    margin: 8px 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }

  &__program-title {
    font-size: 12px;
    font-weight: 300;
    line-height: 14px;
  }
}
</style>
